import { computed, type ComputedRef, type Ref, ref } from 'vue'
import { VueLogger } from 'vue-logger-plugin'

import { HTMLAudioWrapper, PAUSED, PLAYING, SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'

interface AudioPlayerParams {
  segmentIndex: number
  sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios | null
  pitchShift: string
}

const useAudioPlayer = (logger: VueLogger) => {
  const _audioWrapper: Ref<HTMLAudioWrapper | null> = ref(null)
  let _generation = 0
  // Own reactive play-state tracking — HTMLAudioWrapper.playStatus is non-reactive,
  // so we mirror it here so that playing/paused are proper reactive computeds.
  const _playStatus = ref<string | null>(null)
  // True while setLoadPlay is in flight (purge → load → play). Prevents the
  // playing computed from briefly going false during segment-to-segment transitions,
  // which would flash the record button as enabled.
  const _transitioning = ref(false)

  // True when audio is actively playing OR when transitioning between segments.
  const playing: ComputedRef<boolean> = computed(
    () => _transitioning.value || (!!_audioWrapper.value?.audio && _playStatus.value === PLAYING),
  )

  // True when audio is loaded and paused (not during transitions).
  const paused: ComputedRef<boolean> = computed(() => !_transitioning.value && !!_audioWrapper.value?.audio && _playStatus.value === PAUSED)

  function _setPlayStatus(status: string | null) {
    _playStatus.value = status
    if (_audioWrapper.value) {
      _audioWrapper.value.playStatus = status
    }
  }

  function _requireAudio(caller: string): HTMLAudioElement | null {
    if (!_audioWrapper.value) {
      console.log(`[audioPlayer._requireAudio] null _audioWrapper.value in ${caller}`)
      return null
    }
    if (!_audioWrapper.value.audio) {
      console.log(`[audioPlayer._requireAudio] null _audioWrapper.value.audio in ${caller}`)
      return null
    }
    return _audioWrapper.value.audio
  }

  const unloaded = (): boolean => {
    return !_audioWrapper.value || !_audioWrapper.value.audio
  }

  const loaded = (): boolean => {
    return !!_audioWrapper.value?.audio
  }

  const load = (): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const audio = _requireAudio('load')
      if (!audio) {
        reject(new Error('load: _requireAudio returned null'))
        return
      }

      console.log(
        `[audioPlayer.load] readyState=${audio.readyState}, currentTime=${audio.currentTime}, duration=${audio.duration}, src=${audio.src?.substring(0, 60)}`,
      )

      const finish = () => {
        if (!_audioWrapper.value) {
          console.log('[audioPlayer.load] _audioWrapper.value became null during load')
          reject(new Error('load: _audioWrapper.value became null during load'))
          return
        }
        _setPlayStatus(PAUSED)
        resolve()
      }

      // Already fully loaded (cached blob URL). Skip audio.load() which can put
      // the element in a broken state (drops readyState on seek). Just seek to
      // start if needed.
      if (audio.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        console.log(`[audioPlayer.load] already loaded (readyState=${audio.readyState}), skipping audio.load()`)
        if (audio.currentTime > 0.01) {
          console.log(`[audioPlayer.load] seeking from ${audio.currentTime} to 0`)
          audio.onseeked = () => {
            audio.onseeked = null
            console.log(`[audioPlayer.load] seeked complete, readyState=${audio.readyState}, currentTime=${audio.currentTime}`)
            finish()
          }
          audio.currentTime = 0
        } else {
          finish()
        }
        return
      }

      // Not yet loaded: do the full load
      console.log('[audioPlayer.load] calling audio.load()')
      audio.onerror = (e) => {
        console.log('[audioPlayer.load] audio.onerror fired', e)
        reject(new Error(`load: audio error event: ${audio.error?.code} ${audio.error?.message}`))
      }
      audio.load()
      audio.oncanplaythrough = () => {
        console.log(`[audioPlayer.load] oncanplaythrough fired, readyState=${audio.readyState}, currentTime=${audio.currentTime}`)
        audio.oncanplaythrough = null
        finish()
      }
    })
  }

  // Matches master: both onpause and onended resolve. Natural end-of-track fires
  // onended then onpause — both must resolve (second call is a no-op on a settled
  // promise). When pause() interrupts, onpause resolves too; the FSM's playCounter
  // guard prevents stale callbacks from advancing state.
  const play = (): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const audio = _requireAudio('play')
      if (!audio) {
        reject(new Error('play: _requireAudio returned null'))
        return
      }

      console.log(
        `[audioPlayer.play] playStatus=${_playStatus.value}, readyState=${audio.readyState}, paused=${audio.paused}, duration=${audio.duration}`,
      )

      if (_playStatus.value !== PAUSED) {
        console.log(`[audioPlayer.play] REJECTING: not PAUSED, status=${_playStatus.value}`)
        reject(new Error(`play: not PAUSED, status=${_playStatus.value}`))
        return
      }

      let resolved = false
      const onFinish = () => {
        // Guard 1: stale async pause event — _stopCurrentAudio called audio.pause() on
        // this element, then play() was called again on the same element. The queued pause
        // event fires after audio.play() has started, so audio.paused is false.
        if (!audio.paused && !audio.ended) {
          console.log(`[audioPlayer.play] onFinish: IGNORING stale event (audio.paused=${audio.paused}, audio.ended=${audio.ended})`)
          return
        }
        // Guard 2: both onpause and onended can fire for end-of-track — only resolve once.
        if (resolved) {
          return
        }
        resolved = true
        _setPlayStatus(PAUSED)
        resolve()
      }

      audio.onended = () => {
        console.log(`[audioPlayer.play] onended fired, currentTime=${audio.currentTime}`)
        onFinish()
      }
      audio.onpause = () => {
        console.log(`[audioPlayer.play] onpause fired, currentTime=${audio.currentTime}, duration=${audio.duration}, ended=${audio.ended}`)
        if (audio.ended) {
          // Browser edge-case: onpause fires after onended at end-of-track.
          onFinish()
        } else {
          // User pause — leave the promise pending so callers don't auto-advance.
          _setPlayStatus(PAUSED)
        }
      }
      audio.onerror = (e) => {
        console.log('[audioPlayer.play] onerror fired', e)
        reject(new Error(`play: audio error event: ${audio.error?.code} ${audio.error?.message}`))
      }

      console.log('[audioPlayer.play] calling audio.play()')
      const playPromise = audio.play()
      _setPlayStatus(PLAYING)

      if (playPromise) {
        playPromise.catch((err) => {
          console.log(`[audioPlayer.play] audio.play() promise REJECTED: ${err.name}: ${err.message}`)
        })
      }
    })
  }

  const pause = (): void => {
    console.log(`[audioPlayer.pause] called, playStatus=${_playStatus.value}`)
    if (!_requireAudio('pause')) {
      return
    }

    if (_playStatus.value !== PLAYING) {
      console.log('[audioPlayer.pause] not PLAYING, no-op')
      return
    }

    _audioWrapper.value!.audio!.pause()
    // _setPlayStatus(PAUSED) is handled by the onpause event handler in play()
  }

  const getAudioWrapper = (payload: Readonly<AudioPlayerParams>): HTMLAudioWrapper | null => {
    if (!payload.sentenceRecordingMetadataWithPitchedAudios) {
      console.log('[audioPlayer.getAudioWrapper] null sentenceRecordingMetadataWithPitchedAudios')
      return null
    }

    if (payload.sentenceRecordingMetadataWithPitchedAudios.downloading) {
      console.log('[audioPlayer.getAudioWrapper] still downloading')
      return null
    }

    const pitched = payload.sentenceRecordingMetadataWithPitchedAudios.audio[payload.pitchShift]

    if (payload.segmentIndex < 0 || (payload.segmentIndex === 0 && !pitched.segmentHTMLAudioWrappers?.length)) {
      return pitched.sentenceHTMLAudioWrapper
    }

    if (!pitched.segmentHTMLAudioWrappers?.length) {
      return null
    }
    return pitched.segmentHTMLAudioWrappers[payload.segmentIndex]
  }

  const isPlayable = (payload: Readonly<AudioPlayerParams>): boolean => {
    return !!getAudioWrapper(payload)
  }

  const setAudio = (payload: Readonly<AudioPlayerParams>): boolean => {
    console.log(`[audioPlayer.setAudio] segmentIndex=${payload.segmentIndex}, pitchShift=${payload.pitchShift}`)
    const audioWrapper = getAudioWrapper(payload)
    if (!audioWrapper) {
      console.log('[audioPlayer.setAudio] getAudioWrapper returned null')
      return false
    }
    _audioWrapper.value = audioWrapper
    console.log(`[audioPlayer.setAudio] audio.src=${audioWrapper.audio?.src?.substring(0, 60)}, playStatus=${audioWrapper.playStatus}`)
    return true
  }

  // Stop current audio and clear wrapper, but don't touch _transitioning.
  // Used by setLoadPlay (which manages _transitioning itself).
  function _stopCurrentAudio(): void {
    console.log(`[audioPlayer._stopCurrentAudio] has audio=${!!_audioWrapper.value?.audio}, _transitioning=${_transitioning.value}`)
    const audio = _audioWrapper.value?.audio
    if (audio) {
      audio.onpause = null
      audio.onended = null
      audio.onerror = null
      audio.pause()
    }
    _setPlayStatus(null)
    _audioWrapper.value = null
  }

  const setLoadPlay = async (payload: AudioPlayerParams): Promise<void> => {
    _generation++
    const myGeneration = _generation
    _transitioning.value = true
    console.log(
      `[audioPlayer.setLoadPlay] gen=${myGeneration}, segmentIndex=${payload.segmentIndex}, _transitioning=${_transitioning.value}, _playStatus=${_playStatus.value}`,
    )

    if (loaded()) {
      _stopCurrentAudio()
    }

    try {
      if (!setAudio(payload)) {
        throw new Error('setAudio failed')
      }

      await load()

      if (myGeneration !== _generation) {
        console.log(`[audioPlayer.setLoadPlay] gen=${myGeneration} stale (current=${_generation}), bailing`)
        return
      }

      // _transitioning served its purpose — _playStatus takes over from here
      console.log(`[audioPlayer.setLoadPlay] gen=${myGeneration}, load done, clearing _transitioning, about to play()`)
      _transitioning.value = false
      await play()
      console.log(`[audioPlayer.setLoadPlay] gen=${myGeneration}, play() done, _playStatus=${_playStatus.value}`)
    } catch (e) {
      console.log(`[audioPlayer.setLoadPlay] gen=${myGeneration}, CAUGHT: ${e}, current gen=${_generation}`)
      if (myGeneration === _generation) {
        _transitioning.value = false
      }
      throw e
    }
  }

  // Immediately prepare for a new playback: stop current audio and mark as transitioning.
  // Called by higher-level code BEFORE async work (e.g. fetchAudio) so that `playing`
  // becomes true synchronously on user click — no gap where the record button flashes.
  const beginPlayback = (): void => {
    console.log(`[audioPlayer.beginPlayback] _transitioning=${_transitioning.value}, _playStatus=${_playStatus.value}`)
    _generation++
    _transitioning.value = true
    _stopCurrentAudio()
  }

  // Full cleanup: stop audio AND clear transition state.
  // Used by external callers (close overlay, etc.).
  const purge = (): void => {
    console.log(`[audioPlayer.purge] called, _transitioning=${_transitioning.value}, _playStatus=${_playStatus.value}`)
    _stopCurrentAudio()
    _transitioning.value = false
  }

  return {
    beginPlayback,
    isPlayable,
    load,
    loaded,
    pause,
    paused,
    play,
    playing,
    purge,
    setAudio,
    setLoadPlay,
    unloaded,
  }
}

export { AudioPlayerParams, useAudioPlayer }
